/**
 * Contexte global pour le tracking des clics
 * Consomme 13 crédits tous les 100 clics
 * Exempt pour Admin et Super Admin
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CLICKS_THRESHOLD = 100;
const CREDITS_PER_THRESHOLD = 13;
const SYNC_INTERVAL_MS = 30000; // Synchroniser toutes les 30 secondes

const ClickTrackingContext = createContext(null);

export const useClickTracking = () => {
  const context = useContext(ClickTrackingContext);
  if (!context) {
    throw new Error('useClickTracking must be used within a ClickTrackingProvider');
  }
  return context;
};

export const ClickTrackingProvider = ({ children }) => {
  const { user, getAuthHeader } = useAuth();
  const [stats, setStats] = useState({
    clickCount: 0,
    totalClicks: 0,
    clicksUntilCharge: CLICKS_THRESHOLD,
    totalCreditsConsumed: 0,
    creditBalance: 0,
    isAdmin: false
  });
  
  const localClickCount = useRef(0);
  const syncTimeoutRef = useRef(null);
  const lastSyncTime = useRef(0);

  // Vérifier si l'utilisateur est exempt (Admin/Super Admin)
  const isExempt = !user || user.role === 'super_admin' || user.role === 'admin';

  // Récupérer les stats depuis le serveur
  const fetchStats = useCallback(async () => {
    if (!user || isExempt) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/wallets/click-stats`, {
        headers: getAuthHeader()
      });

      if (response.ok) {
        const data = await response.json();
        setStats({
          clickCount: data.click_count || 0,
          totalClicks: data.total_clicks || 0,
          clicksUntilCharge: data.clicks_until_charge || CLICKS_THRESHOLD,
          totalCreditsConsumed: data.total_credits_consumed || 0,
          creditBalance: data.credit_balance || 0,
          isAdmin: data.is_admin || false
        });
      }
    } catch {
    }
  }, [user, isExempt, getAuthHeader]);

  // Synchroniser les clics avec le serveur
  const syncClicks = useCallback(async () => {
    if (!user || isExempt || localClickCount.current === 0) return;

    const clicksToSync = localClickCount.current;
    localClickCount.current = 0;

    try {
      // Envoyer tous les clics accumulés en une seule requête
      for (let i = 0; i < clicksToSync; i++) {
        const response = await fetch(`${BACKEND_URL}/api/wallets/track-click`, {
          method: 'POST',
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          // Mettre à jour les stats locales
          setStats(prev => ({
            ...prev,
            clickCount: data.click_count,
            totalClicks: data.total_clicks,
            clicksUntilCharge: data.clicks_until_charge,
            creditBalance: data.credit_balance
          }));

          // Notifications
          if (data.credits_consumed > 0) {
            toast.info(`🔔 ${data.credits_consumed} crédits utilisés`, {
              description: `100 actions effectuées. Solde: ${data.credit_balance} crédits`,
              duration: 4000
            });
          }

          // Alertes de solde faible
          if (data.credit_balance > 0 && data.credit_balance < 50) {
            toast.warning('⚠️ Solde de crédits faible', {
              description: `Il vous reste ${data.credit_balance} crédits`,
              duration: 5000
            });
          }

          if (data.credit_balance <= 0) {
            toast.error('❌ Crédits épuisés', {
              description: 'Rechargez pour continuer',
              duration: 8000
            });
          }
        }
      }
      
      lastSyncTime.current = Date.now();
    } catch {
      // Remettre les clics non synchronisés
      localClickCount.current += clicksToSync;
    }
  }, [user, isExempt, getAuthHeader]);

  // Fonction principale pour tracker un clic
  const trackClick = useCallback(() => {
    if (isExempt) return;

    // Incrémenter le compteur local
    localClickCount.current += 1;

    // Mettre à jour l'affichage local
    setStats(prev => {
      const newClickCount = prev.clickCount + 1;
      const newClicksUntilCharge = CLICKS_THRESHOLD - (newClickCount % CLICKS_THRESHOLD);
      return {
        ...prev,
        clickCount: newClickCount % CLICKS_THRESHOLD,
        totalClicks: prev.totalClicks + 1,
        clicksUntilCharge: newClicksUntilCharge === CLICKS_THRESHOLD ? 0 : newClicksUntilCharge
      };
    });

    // Planifier la synchronisation avec débounce
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      syncClicks();
    }, 2000); // Synchroniser 2 secondes après le dernier clic

  }, [isExempt, syncClicks]);

  // Charger les stats au montage et quand l'utilisateur change
  useEffect(() => {
    if (user && !isExempt) {
      fetchStats();
    }
  }, [user, isExempt, fetchStats]);

  // Synchronisation périodique
  useEffect(() => {
    if (isExempt) return;

    const interval = setInterval(() => {
      if (localClickCount.current > 0) {
        syncClicks();
      }
    }, SYNC_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isExempt, syncClicks]);

  // Synchroniser avant de quitter la page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (localClickCount.current > 0 && !isExempt) {
        // Envoyer de manière synchrone (best effort)
        navigator.sendBeacon(
          `${BACKEND_URL}/api/wallets/track-click`,
          JSON.stringify({ clicks: localClickCount.current })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isExempt]);

  // Nettoyer au démontage
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      // Tenter une dernière synchro
      if (localClickCount.current > 0 && !isExempt) {
        syncClicks();
      }
    };
  }, [isExempt, syncClicks]);

  const value = {
    trackClick,
    stats,
    isExempt,
    refreshStats: fetchStats,
    threshold: CLICKS_THRESHOLD,
    creditsPerThreshold: CREDITS_PER_THRESHOLD
  };

  return (
    <ClickTrackingContext.Provider value={value}>
      {children}
    </ClickTrackingContext.Provider>
  );
};

export default ClickTrackingContext;
